import Service, { service } from '@ember/service';

// Custom Error class that handles error responses from the Stripe API
export class StripeError extends Error {
  constructor(originalStripeError) {
    super(originalStripeError.message);
    this.name = 'StripeError';
    this.originalStripeError = originalStripeError;
  }

  // returns Sentry context data that will be merged with the context already stored inside the current scope
  toSentryScopeContext() {
    return {
      contexts: {
        stripe: {
          error: this.originalStripeError,
        },
      },
    };
  }
}

export default class extends Service {
  @service store;
  @service('stripev3') stripe;

  /**
   * Charge the user for our service to create their company. This talks
   * to Stripe and to the backend, initiating the process and confirming
   * each step as recommended by Stripe.
   *
   * @method collectInitialDeposit
   * @param  {Object} stripeElement A Stripe "Element", referencing the user's card details.
   * @param  {Object} details
   * @param {String} details.pricePlanId pricePlan chosen by the user. Determines the amount to charge.
   * @param {String} details.registration the current registration to which the plan will be applied.
   * @returns {Promise} Resolves when the process is completed, successfully or otherwise. No value wrapped.
   * @throws {Error} As reported by Stripe when using its `createPaymentMethod` API.
   * @throws {AjaxError} As reported by ember-ajax when talking to the Register service. See https://github.com/ember-cli/ember-ajax#error-handling
   */
  async collectInitialDeposit(stripeElement, { registration, pricePlanId }) {
    let stripeResponse = await this.stripe.createPaymentMethod('card', stripeElement);
    if (stripeResponse.error) {
      throw new StripeError(stripeResponse.error);
    }

    let paymentIntent = this.store.createRecord('payment-intent', {
      registration,
      pricePlanId,
      paymentMethodId: stripeResponse.paymentMethod.id,
    });

    paymentIntent = await paymentIntent.save();

    await this.confirmPaymentIntent(paymentIntent);
  }

  /**
   * Check with Stripe and the backend that the payment has indeed been successful, and
   * run additional checks (eg: 3D Secure) if necessary.
   *
   * Additional confirmation steps will render as modals over our site. No redirects to
   * third party sites or anything like that.
   *
   * Note that this function may be called recursively if the payment provider requires
   * multiple confirmation steps. Stripe's documentation doesn't say explicitly that
   * this is a possibility, but their code examples do provide for it.
   * See https://stripe.com/docs/payments/payment-intents/migration#elements-step-3
   *
   * @method confirmPaymentIntent
   * @private
   * @param {Object} paymentIntent Payment Intent to confirm, as provided by Stripe.
   * @returns {Promise} Resolves when the process is completed, successfully or otherwise. No value wrapped.
   * @throws {StripeError} Error object that wraps Stripe's own error response to `handleCardAction`
   * @throws {AjaxError} As reported by ember-ajax when talking to the Register service. See https://github.com/ember-cli/ember-ajax#error-handling
   */
  async confirmPaymentIntent(paymentIntent) {
    if (paymentIntent.requiresAction) {
      let actionResult = await this.stripe.handleCardAction(paymentIntent.clientSecret);
      if (actionResult.error) {
        throw new StripeError(actionResult.error);
      }

      await paymentIntent.confirm();

      await this.confirmPaymentIntent(paymentIntent);
    }
  }
}
